import { extractFlattenedField } from "./dataExtraction";
import { formatCurrency, formatPercentage } from "./formatting";
import { isValidField } from "./validation";
import { NarrationItem } from "../../Types/WatchlistSummary";

/**
 * Universal Section Audio Extractor with 4-level priority
 */
export function extractSectionAudio(sectionId: string, data: any): string {
  // Priority 1: Root-level synopsis
  const rootSynopsis = extractFlattenedField(data, "synopsis", null);
  if (isValidField(rootSynopsis)) {
    return rootSynopsis;
  }

  // Priority 2: Section-specific synopsis (e.g., data.financial.synopsis)
  const sectionSynopsis = extractFlattenedField(
    data,
    `${sectionId}.synopsis`,
    null
  );
  if (isValidField(sectionSynopsis)) {
    return sectionSynopsis;
  }

  // Priority 3: Legacy audio_summary (root)
  const rootAudioSummary = extractFlattenedField(data, "audio_summary", null);
  if (isValidField(rootAudioSummary)) {
    return rootAudioSummary;
  }

  // Priority 4: Section-specific audio_summary
  const sectionAudioSummary = extractFlattenedField(
    data,
    `${sectionId}.audio_summary`,
    null
  );
  if (isValidField(sectionAudioSummary)) {
    return sectionAudioSummary;
  }

  // Fallback: Use specific extractors
  switch (sectionId) {
    case "financial":
      return extractFinancialAudio(data);
    case "news":
      return extractNewsAudio(data);
    case "leadership":
      return extractLeadershipAudio(data);
    case "social":
      return extractSocialAudio(data);
    case "traffic":
      return extractDigitalAudio(data);
    case "growth":
      return extractGrowthAudio(data);
    case "legal":
      return extractLegalAudio(data);
    case "stack":
      return extractStackAudio(data);
    default:
      return extractGenericSectionAudio(data, sectionId);
  }
}

/**
 * Financial Audio Extraction
 */
export function extractFinancialAudio(data: any): string {
  // Priority 1: Direct synopsis field
  const directSynopsis = extractFlattenedField(data, "synopsis", null);
  if (isValidField(directSynopsis)) {
    return directSynopsis;
  }

  // Priority 2: Nested financial synopsis
  const financialSynopsis = extractFlattenedField(
    data,
    "financial.synopsis",
    null
  );
  if (isValidField(financialSynopsis)) {
    return financialSynopsis;
  }

  // Priority 3: Legacy audio_summary field
  const legacyAudioSummary = extractFlattenedField(data, "audio_summary", null);
  if (isValidField(legacyAudioSummary)) {
    return legacyAudioSummary;
  }

  // Priority 4: Nested financial audio_summary
  const financialAudioSummary = extractFlattenedField(
    data,
    "financial.audio_summary",
    null
  );
  if (isValidField(financialAudioSummary)) {
    return financialAudioSummary;
  }

  // Fallback: Construct from available financial data
  let narrative = "";

  const revenue = extractFlattenedField(
    data,
    "financial_statements.income.revenue",
    null
  );
  const netIncome = extractFlattenedField(
    data,
    "financial_statements.income.netIncome",
    null
  );
  const growth = extractFlattenedField(
    data,
    "fundamentals.earnings.earnings_growth.annual",
    null
  );
  const marketCap = extractFlattenedField(data, "stockData.marketCap", null);
  const currentPrice = extractFlattenedField(
    data,
    "stockData.currentPrice",
    null
  );

  if (isValidField(revenue)) {
    narrative += `Revenue: ${formatCurrency(revenue)}. `;
  }

  if (isValidField(netIncome)) {
    narrative += `Net income: ${formatCurrency(netIncome)}. `;
  }

  if (isValidField(growth)) {
    narrative += `Annual growth: ${formatPercentage(growth)}. `;
  }

  if (isValidField(marketCap)) {
    narrative += `Market cap: ${formatCurrency(marketCap)}. `;
  }

  if (isValidField(currentPrice)) {
    narrative += `Current price: ${formatCurrency(currentPrice)}. `;
  }

  return (
    narrative ||
    "Financial data is being processed. Please check back later for updated information."
  );
}

/**
 * Financial Modal Audio Extraction
 */
export function extractFinancialModalAudio(
  data: any,
  organizationName?: string
): string {
  const baseAudio = extractFinancialAudio(data);

  if (!organizationName) {
    return baseAudio;
  }

  let audioContent = baseAudio;

  // Add organization-specific relevance context if available
  const relevance = extractFlattenedField(
    data,
    "organizationContext.relevantToOrganization.relevance",
    null
  );
  if (relevance) {
    audioContent += ` For ${organizationName}, ${relevance}`;
  }

  return audioContent;
}

/**
 * News Audio Extraction
 */
export function extractNewsAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "news.synopsis", null);
  if (isValidField(synopsis)) return synopsis;

  let narrative = "";
  const summary =
    extractFlattenedField(data, "summary", null) ||
    extractFlattenedField(data, "overview", null);
  if (isValidField(summary)) narrative += summary;

  const headlines = extractFlattenedField(data, "recent_headlines", []);
  if (Array.isArray(headlines) && headlines.length > 0) {
    const validHeadlines = headlines.filter(h => isValidField(h)).slice(0, 3);
    if (validHeadlines.length > 0) {
      narrative += ` Recent headlines include: ${validHeadlines.join(". ")}.`;
    }
  }

  return (
    narrative ||
    "News analysis is being processed. Please check back later for updated information."
  );
}

/**
 * News Modal Audio Extraction
 */
export function extractNewsModalAudio(
  data: any,
  organizationName?: string
): string {
  let audioContent = "";

  const synopsis = extractFlattenedField(data, "synopsis", null);
  if (isValidField(synopsis)) {
    audioContent = synopsis;
  }

  const totalArticles = extractFlattenedField(
    data,
    "newsMetrics.totalArticles",
    null
  );
  const mediaVelocity = extractFlattenedField(
    data,
    "newsMetrics.mediaVelocity",
    null
  );
  const overallSentiment = extractFlattenedField(
    data,
    "sentimentAnalysis.overallSentiment",
    null
  );

  const metricsInfo = [];
  if (isValidField(totalArticles))
    metricsInfo.push(`${totalArticles} articles analyzed`);
  if (isValidField(mediaVelocity))
    metricsInfo.push(`${mediaVelocity} media velocity`);
  if (isValidField(overallSentiment))
    metricsInfo.push(`${overallSentiment} sentiment`);

  if (metricsInfo.length > 0) {
    if (audioContent) audioContent += ". ";
    audioContent += `News metrics show: ${metricsInfo.join(", ")}.`;
  }

  if (organizationName) {
    const relevance =
      extractFlattenedField(
        data,
        "organizationContext.relevantToOrganization.relevance",
        null
      ) ||
      extractFlattenedField(
        data,
        "organizationContext.relevantToOrganization",
        null
      );
    if (isValidField(relevance)) {
      if (audioContent) audioContent += " ";
      audioContent += `This is particularly relevant to ${organizationName}: ${relevance}`;
    }
  }

  return audioContent || "News analysis summary not available.";
}

/**
 * Leadership Audio Extraction
 */
export function extractLeadershipAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "leadership.synopsis", null);

  if (isValidField(synopsis)) return synopsis;

  let narrative = "";

  const changes =
    extractFlattenedField(data, "executive_changes", []) ||
    extractFlattenedField(data, "executiveChanges", []);
  if (Array.isArray(changes) && changes.length > 0) {
    narrative += `Recent leadership changes include ${changes.length} executive updates. `;
  }

  const trends = extractFlattenedField(data, "trends", null);
  if (isValidField(trends)) {
    narrative += `Leadership trends show ${trends}. `;
  }

  return (
    narrative ||
    "Leadership analysis is being processed. Please check back later for updated information."
  );
}

/**
 * Social Audio Extraction
 */
export function extractSocialAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "social.synopsis", null);
  if (isValidField(synopsis)) return synopsis;

  let narrative = "";
  const engagement = extractFlattenedField(data, "engagement_metrics", null);
  if (isValidField(engagement)) {
    narrative += `Social media engagement shows ${engagement}. `;
  }

  const sentiment = extractFlattenedField(data, "sentiment_analysis", null);
  if (isValidField(sentiment)) {
    narrative += `Overall sentiment is ${sentiment}. `;
  }

  return (
    narrative ||
    "Social media analysis is being processed. Please check back later for updated information."
  );
}

/**
 * Social Modal Audio Extraction
 */
export function extractSocialModalAudio(
  data: any,
  organizationName?: string
): string {
  const baseAudio = extractSocialAudio(data);

  if (!organizationName) {
    return baseAudio;
  }

  let audioContent = baseAudio;

  const orgRelevance = extractFlattenedField(
    data,
    "organizationMentions.relevance",
    null
  );
  if (isValidField(orgRelevance)) {
    audioContent += ` This is particularly relevant to ${organizationName}: ${orgRelevance}`;
  }

  return audioContent;
}

/**
 * Digital/Traffic Audio Extraction
 */
export function extractDigitalAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "traffic.synopsis", null);
  if (isValidField(synopsis)) return synopsis;

  let narrative = "";
  const visits = extractFlattenedField(data, "website_visits", null);
  if (isValidField(visits)) {
    narrative += `Website traffic shows ${visits} visits. `;
  }

  const trends = extractFlattenedField(data, "traffic_trends", null);
  if (isValidField(trends)) {
    narrative += `Digital trends indicate ${trends}. `;
  }

  return (
    narrative ||
    "Digital traffic analysis is being processed. Please check back later for updated information."
  );
}

/**
 * Traffic Modal Audio Extraction
 */
export function extractTrafficModalAudio(
  data: any,
  accountName?: string
): string {
  let audioContent = "";

  const synopsis = extractFlattenedField(data, "synopsis", null);
  if (synopsis) {
    audioContent += synopsis;
  }

  const monthlyVisits = extractFlattenedField(
    data,
    "metrics.monthly_visits",
    null
  );
  const monthlyChangePct = extractFlattenedField(
    data,
    "metrics.monthly_change_pct",
    null
  );
  const globalRankCurrent = extractFlattenedField(
    data,
    "metrics.global_rank.current",
    null
  );

  if (monthlyVisits) {
    const formattedVisits =
      monthlyVisits >= 1000000
        ? `${(monthlyVisits / 1000000).toFixed(1)} million`
        : monthlyVisits >= 1000
          ? `${(monthlyVisits / 1000).toFixed(1)} thousand`
          : monthlyVisits.toString();

    audioContent += ` The website receives approximately ${formattedVisits} monthly visits`;

    if (monthlyChangePct) {
      const changeDirection = monthlyChangePct > 0 ? "increase" : "decrease";
      audioContent += `, showing a ${Math.abs(monthlyChangePct).toFixed(1)}% ${changeDirection} in traffic`;
    }

    if (globalRankCurrent) {
      audioContent += ` and ranks number ${globalRankCurrent.toLocaleString()} globally`;
    }

    audioContent += ".";
  }

  return (
    audioContent.trim() ||
    `Digital traffic analysis for ${accountName || "this account"}.`
  );
}

/**
 * Growth Audio Extraction
 */
export function extractGrowthAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "growth.synopsis", null);
  if (isValidField(synopsis)) return synopsis;

  let narrative = "";
  const signals = extractFlattenedField(data, "growth_signals", []);
  if (Array.isArray(signals) && signals.length > 0) {
    narrative += `Growth indicators show ${signals.length} positive signals. `;
  }

  const metrics = extractFlattenedField(data, "key_metrics", null);
  if (isValidField(metrics)) {
    narrative += `Key growth metrics: ${metrics}. `;
  }

  return (
    narrative ||
    "Growth analysis is being processed. Please check back later for updated information."
  );
}

/**
 * Growth Modal Audio Extraction
 */
export function extractGrowthModalAudio(
  data: any,
  organizationName?: string
): string {
  const baseAudio = extractGrowthAudio(data);

  if (!organizationName) {
    return baseAudio;
  }

  let audioContent = baseAudio;

  const relationshipInsights = extractFlattenedField(
    data,
    "relationshipInsights",
    []
  );
  if (Array.isArray(relationshipInsights) && relationshipInsights.length > 0) {
    const relevantInsight = relationshipInsights.find(insight =>
      isValidField(insight?.description)
    );
    if (relevantInsight) {
      audioContent += ` For ${organizationName}, ${relevantInsight.description}`;
    }
  }

  return audioContent;
}

/**
 * Legal Audio Extraction
 */
export function extractLegalAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "legal.synopsis", null);
  if (isValidField(synopsis)) return synopsis;

  let narrative = "";
  const issues = extractFlattenedField(data, "legal_issues", []);
  if (Array.isArray(issues) && issues.length > 0) {
    narrative += `Legal developments include ${issues.length} items of note. `;
  }

  const compliance = extractFlattenedField(data, "compliance_status", null);
  if (isValidField(compliance)) {
    narrative += `Compliance status: ${compliance}. `;
  }

  return (
    narrative ||
    "Legal analysis is being processed. Please check back later for updated information."
  );
}

/**
 * Legal Modal Audio Extraction
 */
export function extractLegalModalAudio(
  data: any,
  organizationName?: string
): string {
  const synopsis = extractFlattenedField(data, "synopsis", null);
  const activeCases = extractFlattenedField(
    data,
    "kpis.active_cases_count",
    null
  );
  const orgRelevance = extractFlattenedField(data, "organization_related", []);

  let content = synopsis || "Legal and regulatory analysis for this account.";

  if (isValidField(activeCases)) {
    content += ` Currently tracking ${activeCases} active cases.`;
  }

  if (
    Array.isArray(orgRelevance) &&
    orgRelevance.length > 0 &&
    organizationName
  ) {
    const relevanceText =
      orgRelevance[0]?.description ||
      "Legal compliance considerations present opportunities for enhanced oversight solutions.";
    content += ` Relevance to ${organizationName}: ${relevanceText}`;
  }

  return content;
}

/**
 * Stack Audio Extraction
 */
export function extractStackAudio(data: any): string {
  const synopsis =
    extractFlattenedField(data, "synopsis", null) ||
    extractFlattenedField(data, "stack.synopsis", null);
  if (isValidField(synopsis)) return synopsis;

  let narrative = "";
  const technologies = extractFlattenedField(data, "technologies", []);
  if (Array.isArray(technologies) && technologies.length > 0) {
    narrative += `Technology stack includes ${technologies.length} key components. `;
  }

  const changes = extractFlattenedField(data, "tech_changes", null);
  if (isValidField(changes)) {
    narrative += `Recent technology changes: ${changes}. `;
  }

  return (
    narrative ||
    "Technology stack analysis is being processed. Please check back later for updated information."
  );
}

/**
 * Generic Section Audio Extraction
 */
export function extractGenericSectionAudio(
  data: any,
  sectionId: string
): string {
  const commonFields = [
    "synopsis",
    "summary",
    "description",
    "content",
    "overview",
  ];

  for (const field of commonFields) {
    const value = extractFlattenedField(data, field, null);
    if (isValidField(value)) {
      return value;
    }
  }

  if (data && typeof data === "object") {
    const keys = Object.keys(data);
    const meaningfulData = keys
      .filter(key => isValidField(data[key]))
      .slice(0, 3)
      .map(key => `${key}: ${data[key]}`)
      .join(". ");

    if (meaningfulData) {
      return meaningfulData;
    }
  }

  return `${sectionId} analysis is being processed. Please check back later for updated information.`;
}

/**
 * Overall Modal Audio Extraction
 */
export function extractOverallModalAudio(
  data: any,
  organizationName?: string
): string {
  let audioContent = "";

  const summaryStatement = extractFlattenedField(
    data,
    "summaryStatement",
    null
  );
  if (isValidField(summaryStatement)) {
    audioContent = summaryStatement;
  }

  if (organizationName) {
    const relevanceStatement = extractFlattenedField(
      data,
      "relevanceStatement",
      null
    );
    if (isValidField(relevanceStatement)) {
      audioContent += audioContent
        ? ` ${relevanceStatement}`
        : relevanceStatement;
    }

    const relevance = extractFlattenedField(
      data,
      "organizationContext.relevantToOrganization.relevance",
      null
    );
    if (isValidField(relevance)) {
      audioContent += ` This is particularly relevant to ${organizationName}: ${relevance}`;
    }
  }

  return audioContent || "Overall analysis summary not available.";
}

/**
 * About Modal Audio Extraction
 */
export function extractAboutModalAudio(
  data: any,
  companyName?: string,
  organizationName?: string
): string {
  let audioContent = "";

  const synopsis = extractFlattenedField(data, "synopsis", null);
  if (isValidField(synopsis)) {
    audioContent = synopsis;
  }

  const mission = extractFlattenedField(data, "mission", null);
  if (isValidField(mission) && mission !== synopsis) {
    if (audioContent) audioContent += ". ";
    audioContent += `${companyName || "The company"}'s mission: ${mission}`;
  }

  return (
    audioContent ||
    `About information for ${companyName || "this company"} is being processed.`
  );
}

/**
 * Build Dynamic Narration Array
 */
export function buildDynamicNarrationArray(
  digestData: any,
  organizationName?: string,
  briefingDate?: string
): NarrationItem[] {
  const narrationArray: NarrationItem[] = [];

  const companyName =
    extractCompanyNameFromDigest(digestData) || organizationName || "Company";

  let dateText = "";
  if (briefingDate) {
    try {
      const dateObj = new Date(briefingDate + "T00:00:00");
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      dateText = `, dated ${dateObj.toLocaleDateString("en-US", options)}`;
    } catch (error) {
      dateText = `, dated ${briefingDate}`;
    }
  }

  // Welcome message
  const welcomeContent = `Let us begin with the latest intelligence on ${companyName}${dateText}.`;
  narrationArray.push({
    type: "welcome",
    sectionId: "welcome",
    subsectionId: null,
    title: "Welcome to Izba Intelligence Briefing!",
    content: welcomeContent,
    autoAdvance: true,
  });

  // Overall Summary
  const overallSummaryContent = extractOverallSummaryContent(
    digestData,
    organizationName
  );
  if (overallSummaryContent) {
    narrationArray.push({
      type: "briefingSummary",
      sectionId: "summary",
      subsectionId: "overall",
      title: `Overall Summary on ${companyName}`,
      content: overallSummaryContent,
      autoAdvance: true,
    });
  }

  return narrationArray;
}

// Helper functions
function extractCompanyNameFromDigest(digestData: any): string {
  if (!digestData) return "";

  const nameSources = [
    digestData?.about?.name,
    digestData?.about?.about?.name,
    digestData?.About?.about?.name,
    digestData?.About?.name,
  ];

  for (const name of nameSources) {
    if (name && typeof name === "string" && name.trim()) {
      return name.trim();
    }
  }

  return "";
}

function extractOverallSummaryContent(
  digestData: any,
  organizationName?: string
): string {
  if (!digestData?.summary?.overallAccountUpdate) {
    return "";
  }

  const overallUpdate = digestData.summary.overallAccountUpdate;
  const summaryStatement = overallUpdate.summaryStatement || "";
  const relevanceStatement = overallUpdate.relevanceStatement || "";

  if (!summaryStatement && !relevanceStatement) return "";

  let content = "";
  if (summaryStatement) content += summaryStatement;

  if (relevanceStatement && organizationName) {
    if (content) content += ". ";
    content += `Relevance to ${organizationName}. ${relevanceStatement}`;
  }

  return content;
}
